import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import type { GenerateBriefRequest } from '../../types'

const SECTORS = [
  'Retail & consumer',
  'Healthcare',
  'Public sector',
  'Financial services',
  'Insurance',
  'Betting & gaming',
  'Technology',
]

const CHIPS = [
  { c: 'Tesco', s: 'Retail & consumer', w: 'tesco.com', label: 'Tesco' },
  { c: 'Marks & Spencer', s: 'Retail & consumer', w: 'marksandspencer.com', label: 'M&S' },
  { c: 'JD Sports', s: 'Retail & consumer', w: 'jdsports.co.uk', label: 'JD Sports' },
  { c: 'Currys', s: 'Retail & consumer', w: 'currys.co.uk', label: 'Currys' },
  { c: 'Boots', s: 'Retail & consumer', w: 'boots.com', label: 'Boots' },
]

/** Rotating status while the model researches — the call takes a while and silence reads as a hang. */
const LOAD_MSGS: [string, string][] = [
  ['Researching the organisation…', 'Scanning recent signals across the web'],
  ['Reading strategic priorities…', 'Annual reports, news and announcements'],
  ['Detecting transformation signals…', 'Cloud, data, AI, loyalty and delivery cues'],
  ['Matching Burendo case studies…', 'Scoring relevance across the library'],
  ['Scoring the opportunity…', 'Weighing signals against the sector model'],
  ['Running the persuasion engine…', 'Scoring 5 outreach styles, picking the winner'],
]

export function ResearchView() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { can } = useAuth()

  const [company, setCompany] = useState('')
  const [website, setWebsite] = useState('')
  const [sector, setSector] = useState(SECTORS[0])

  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState(0)
  const [error, setError] = useState('')

  const prefilled = useRef(false)

  /*
   * Deep link from the Accounts table or the global search: prefill the form, but do NOT fire
   * the research call.
   *
   * The prototype generated immediately on a row click. With 2,811 organisations in the table
   * that turns a single misclick into a billed Anthropic call, so the user now confirms by
   * pressing Generate. One extra click; no accidental spend.
   */
  useEffect(() => {
    const c = params.get('company')
    if (!c || prefilled.current) return
    prefilled.current = true

    const s = params.get('sector')
    setCompany(c)
    setWebsite(params.get('website') ?? '')
    if (s && SECTORS.includes(s)) setSector(s)
  }, [params])

  useEffect(() => {
    if (!busy) return
    const timer = window.setInterval(() => setStep((i) => Math.min(i + 1, LOAD_MSGS.length - 1)), 3500)
    return () => window.clearInterval(timer)
  }, [busy])

  const canResearch = can('RESEARCHER')

  async function generate(req: GenerateBriefRequest) {
    if (!req.company.trim()) {
      setError('Enter a company name to generate a brief.')
      return
    }
    setError('')
    setBusy(true)
    setStep(0)
    try {
      const brief = await api.generateBrief(req)
      navigate(`/accounts/${encodeURIComponent(brief.company)}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="view on">
      <div className="eyebrow">Research</div>
      <h1 className="disp">Briefs</h1>
      <p className="lede">
        Type an organisation and get a structured account brief — strategic priorities, transformation
        signals, an opportunity score, matching Burendo case studies, the buyers to target and a
        first-contact email.
      </p>

      {!canResearch && (
        <div className="err on" style={{ marginTop: 22 }}>
          You have read-only access. Generating a brief makes a billed research call, which needs the
          <b> Researcher</b> role — ask an administrator to grant it in Entra ID.
        </div>
      )}

      <div className="panel gen-card">
        <div className="grid3">
          <div>
            <label className="fld" htmlFor="i-name">
              Company name
            </label>
            <input
              className="tx"
              id="i-name"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && generate({ company, website, sector })}
              placeholder="e.g. Tesco"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="fld" htmlFor="i-web">
              Website
            </label>
            <input
              className="tx"
              id="i-web"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="tesco.com (optional)"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="fld" htmlFor="i-sec">
              Sector
            </label>
            <select className="tx" id="i-sec" value={sector} onChange={(e) => setSector(e.target.value)}>
              {SECTORS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="chips">
          <span className="lbl">Try:</span>
          {CHIPS.map((chip) => (
            <span
              key={chip.c}
              className="chip"
              onClick={() => {
                setCompany(chip.c)
                setWebsite(chip.w)
                setSector(chip.s)
              }}
            >
              {chip.label}
            </span>
          ))}
        </div>

        <button
          className="genbtn"
          id="go"
          disabled={busy || !canResearch}
          onClick={() => generate({ company, website, sector })}
        >
          {busy ? 'Generating…' : 'Generate account brief →'}
        </button>
      </div>

      {error && (
        <div className="err on" role="alert">
          {error}
        </div>
      )}

      {busy && (
        <div className="loading on">
          <div className="ring" />
          <div className="lmsg">{LOAD_MSGS[step][0]}</div>
          <div className="lstep">{LOAD_MSGS[step][1]}</div>
        </div>
      )}

      <p className="note">
        Case studies, the four-capability framework and sector signal weights are seeded from the Burendo
        decks and stored centrally. Briefs are shared with the whole team and saved to the database.
      </p>
    </div>
  )
}
